import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useFormik, setNestedObjectValues, yupToFormErrors } from 'formik';
import { Stepper, Container, Step, StepLabel, Typography, Card, CardContent, Button, Stack, Box } from '@mui/material';

import { PreferredAssessmentDates } from '../../request/steps';
import { GTag } from '../../../ga';
import { BulkRequest, Test, type AddBulkRequestProps, isLocationOnsite } from '../../../models';
import { BulkRequestTest, BulkRequestReview, BulkRequestLocation } from '../steps';
import { CustomStepIcon, CustomStepConnector } from '../../../components/stepper';
import { useAddBulkRequestMutation, usePayBulkRequestMutation } from '../../../slices/apis/bulkRequestApi';
import { useGetSuppliersQuery } from '../../../slices/apis/testingCentreApi';

const steps = [
	{
		name: 'Location',
		defaultValue: BulkRequest.locationSchema.getDefault(),
	},
	{
		name: 'Tests',
		defaultValue: BulkRequest.testsSchema().getDefault(),
	},
	{
		name: 'Preferred Assessment Date',
		defaultValue: BulkRequest.preferredAssessmentDateSchema.getDefault(),
	},
	{
		name: 'Review',
		defaultValue: BulkRequest.reviewSchema.getDefault(),
		resetOnNavBack: true,
	},
];

const BulkRequestNew = () => {
	const [activeStep, setActiveStep] = useState(0);
	const [disableSubmit, setDisableSubmit] = useState(false);

	const { data: testingCentres } = useGetSuppliersQuery();
	const [
		addBulkRequest,
		{
			data: addBulkRequestResponse,
			isSuccess: isAddBulkRequestSuccess,
			isLoading: isAddBulkRequestLoading,
			isError: isAddBulkRequestError,
		},
	] = useAddBulkRequestMutation();
	const [
		payBulkRequest,
		{
			isSuccess: isPayBulkRequestSuccess,
			isLoading: isPayBulkRequestLoading,
			data: payBulkRequestResponse,
			error: payBulkRequestError,
		},
	] = usePayBulkRequestMutation();

	const getStepSchema = (values: AddBulkRequestProps) => {
		const isOnsiteLocation = testingCentres ? isLocationOnsite(testingCentres, values.location) : false;

		switch (activeStep) {
			case 0:
				return BulkRequest.locationSchema;
			case 1:
				return BulkRequest.testsSchema({ isOnsiteLocation });
			case 2:
				return BulkRequest.preferredAssessmentDateSchema;
			case 3:
				return BulkRequest.reviewSchema;
			default:
				return BulkRequest.addSchema;
		}
	};

	const handleBack = () => {
		if (steps[activeStep].resetOnNavBack) {
			formik.resetForm({
				values: {
					...formik.values,
					...steps[activeStep].defaultValue,
				},
			});
		}

		setActiveStep((currStep) => currStep - 1);
	};

	const formik = useFormik<AddBulkRequestProps>({
		initialValues: BulkRequest.addSchema.getDefault(),
		validate: async (values) => {
			try {
				await getStepSchema(values).validate(values, { abortEarly: false });
				return {};
			} catch (err) {
				return yupToFormErrors(err);
			}
		},
		onSubmit: async (values) => {
			await addBulkRequest({
				...values,
				tests: Test.removeNilTest(values.tests),
			});

			GTag('event', 'form_submit_new_bulk_request');
		},
	});

	const onNextClick = async () => {
		const validationErrors = await formik.validateForm();
		if (Object.keys(validationErrors).length > 0) {
			formik.setTouched(setNestedObjectValues(validationErrors, true));
		} else {
			setActiveStep((currStep) => currStep + 1);
		}
	};

	useEffect(() => {
		// disable submit in a hope to prevent duplicated requests being submitted
		if (isAddBulkRequestSuccess) {
			setDisableSubmit(true);
		}

		// disable submit when encounter an submission error
		if (isAddBulkRequestError) {
			setDisableSubmit(true);
		}

		if (isAddBulkRequestSuccess && !isPayBulkRequestLoading && !payBulkRequestError) {
			const { id } = addBulkRequestResponse;

			payBulkRequest({
				id,
				amount: formik.values.amount,
			});
		}

		if (isPayBulkRequestSuccess && payBulkRequestResponse) {
			GTag('event', 'payment_success_bulk_request');

			window.location = payBulkRequestResponse.url;
		}
	}, [
		formik,
		isAddBulkRequestSuccess,
		isPayBulkRequestSuccess,
		isPayBulkRequestLoading,
		isAddBulkRequestError,
		addBulkRequestResponse,
		payBulkRequest,
		payBulkRequestResponse,
		payBulkRequestError,
	]);

	const formContent = (step: number) => {
		switch (step) {
			case 0:
				return <BulkRequestLocation formik={formik} />;
			case 1:
				return <BulkRequestTest formik={formik} allowQtyEditing />;
			case 2:
				return (
					<PreferredAssessmentDates
						formik={formik}
						prefAssessmentDateLabels={[
							'Preferred Assessment Date',
							'Second Preferred Assessment Date',
							'Third Preferred Assessment Date',
						]}
					/>
				);
			case 3:
				return <BulkRequestReview formik={formik} />;
			default:
				return <div>404: Not Found</div>;
		}
	};

	return (
		<>
			<Helmet>
				<title> New Bulk Request - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Container maxWidth="lg">
				<Typography variant="h6" align="center">
					New Bulk Request
				</Typography>
				<form onSubmit={formik.handleSubmit}>
					<Card sx={{ marginTop: 2 }}>
						<CardContent sx={{ p: { xs: 1, md: 4 }, display: 'flex', flexDirection: 'column' }}>
							<Stepper
								activeStep={activeStep}
								orientation="horizontal"
								alternativeLabel
								connector={<CustomStepConnector />}
								sx={{ mt: 3 }}
							>
								{steps.map((step, index) => (
									<Step key={step.name}>
										<StepLabel slots={{ stepIcon: CustomStepIcon }}>{step.name}</StepLabel>
									</Step>
								))}
							</Stepper>
							<Box sx={{ mb: 2, mt: 4, mx: { xs: 0, md: 10 } }}>
								<Box sx={{ height: 480, overflowY: 'auto', overflowX: 'hidden', pr: 1, scrollbarGutter: 'stable' }}>
									{formContent(activeStep)}
								</Box>
							</Box>
							<Box
								sx={{ mt: { xs: 2, md: 6 }, mb: 3, px: { xs: 0, md: 10 }, display: 'flex', justifyContent: 'flex-end' }}
							>
								<Stack direction="row" spacing={4}>
									<Button
										disabled={activeStep === 0}
										onClick={handleBack}
										type="button"
										variant="outlined"
										color="primary"
									>
										Back
									</Button>
									{activeStep === steps.length - 1 ? (
										<Button
											loading={isAddBulkRequestLoading || isPayBulkRequestLoading}
											variant="contained"
											color="brand"
											type="submit"
											disabled={disableSubmit}
											// don't remove key or formik will submit the form even before submit button is clicked
											key="submit"
										>
											<span>Submit</span>
										</Button>
									) : (
										// don't remove key or formik will submit the form even before submit button is clicked
										<Button key="next" variant="contained" color="primary" onClick={onNextClick} type="button">
											Next
										</Button>
									)}
								</Stack>
							</Box>
						</CardContent>
					</Card>
				</form>
			</Container>
		</>
	);
};

export default BulkRequestNew;
